import { useAuthStore } from "@/stores/auth";

// Security configuration - Set to false to disable all security features
const SECURITY_ENABLED = true; // Change this to true to enable security

// Advanced security measures for non-authenticated users
export class SecurityManager {
  constructor() {
    this.originalConsole = { ...console };
    this.securityActive = false;
    this.observers = [];
    this.intervals = [];
    this.authStore = null;
  }

  // Obfuscate console for non-authenticated users
  obfuscateConsole() {
    if (!this.authStore?.isAuthenticated) {
      // Override console methods
      console.log = () => {};
      console.warn = () => {};
      console.error = () => {};
      console.info = () => {};
      console.debug = () => {};
      console.clear = () => {};
      console.dir = () => {};
      console.dirxml = () => {};
      console.group = () => {};
      console.groupCollapsed = () => {};
      console.groupEnd = () => {};
      console.table = () => {};
      console.time = () => {};
      console.timeEnd = () => {};
      console.trace = () => {};

      // Add fake console messages to confuse
      const fakeMessages = [
        "System initialized successfully",
        "Loading security protocols...",
        "Authentication required for access",
        "Monitoring active sessions...",
      ];

      setTimeout(() => {
        fakeMessages.forEach((msg) => {
          this.originalConsole.log(
            `%c${msg}`,
            "color: green; font-weight: bold;"
          );
        });
      }, 1000);
    }
  }

  // Restore console for authenticated users
  restoreConsole() {
    Object.assign(console, this.originalConsole);
  }

  // Monitor for debugging attempts
  startDebuggingDetection() {
    if (!this.authStore?.isAuthenticated) {
      // Monitor for breakpoints
      const startTime = performance.now();

      const checkDebugging = () => {
        const currentTime = performance.now();
        if (currentTime - startTime > 100) {
          this.handleSecurityBreach("Debugging detected");
        }
      };

      // Check multiple times
      const interval = setInterval(() => {
        const start = performance.now();
        debugger; // This will pause if dev tools are open
        const end = performance.now();

        if (end - start > 100) {
          this.handleSecurityBreach("Developer tools detected");
        }
      }, 2000);

      this.intervals.push(interval);
    }
  }

  // Handle security breaches
  handleSecurityBreach(reason) {
    console.clear();

    // Redirect to login or show warning
    if (window.location.pathname !== "/borenn") {
      // Create a full-screen overlay
      const overlay = document.createElement("div");
      overlay.id = "security-overlay";
      overlay.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: #12121c;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Arial', sans-serif;
          z-index: 999999;
          flex-direction: column;
        ">
          <div style="text-align: center; max-width: 500px; padding: 40px;">
            <div style="font-size: 72px; margin-bottom: 20px;">🔒</div>
            <h1 style="font-size: 32px; margin-bottom: 20px; font-weight: 300;">Access Restricted</h1>
            <p style="font-size: 18px; margin-bottom: 30px; opacity: 0.9; line-height: 1.6;">
              This content is protected. Please sign in to access the full website features.
            </p>
            <div style="margin-bottom: 30px; padding: 20px; background: rgba(255,255,255,0.1); border-radius: 10px;">
              <p style="font-size: 14px; margin: 0; opacity: 0.8;">
                Security Event: ${reason}
              </p>
            </div>
           
            <button onclick="window.location.reload()" style="
              background: transparent;
              border: 2px solid rgba(255,255,255,0.3);
              color: white;
              padding: 15px 30px;
              border-radius: 30px;
              cursor: pointer;
              font-size: 16px;
              transition: all 0.3s ease;
            " onmouseover="this.style.borderColor='rgba(255,255,255,0.6)'" onmouseout="this.style.borderColor='rgba(255,255,255,0.3)'">
              Refresh
            </button>
          </div>
        </div>
      `;

      // Remove existing overlay if any
      const existingOverlay = document.getElementById("security-overlay");
      if (existingOverlay) {
        existingOverlay.remove();
      }

      document.body.appendChild(overlay);

      // Blur the background content
      const appElement = document.getElementById("app");
      if (appElement) {
        appElement.style.filter = "blur(10px)";
        appElement.style.pointerEvents = "none";
      }
    }
  }

  // Start monitoring window size changes (dev tools detection)
  monitorWindowSize() {
    if (!this.authStore?.isAuthenticated) {
      let lastInnerWidth = window.innerWidth;
      let lastInnerHeight = window.innerHeight;

      const checkResize = () => {
        const widthDiff = Math.abs(window.innerWidth - lastInnerWidth);
        const heightDiff = Math.abs(window.innerHeight - lastInnerHeight);

        // If significant size change detected (possible dev tools opening)
        if (widthDiff > 200 || heightDiff > 200) {
          this.handleSecurityBreach("Window size manipulation detected");
        }

        lastInnerWidth = window.innerWidth;
        lastInnerHeight = window.innerHeight;
      };

      window.addEventListener("resize", checkResize);

      // Store for cleanup
      this.cleanupFunctions = this.cleanupFunctions || [];
      this.cleanupFunctions.push(() =>
        window.removeEventListener("resize", checkResize)
      );
    }
  }

  // Disable common keyboard shortcuts
  disableShortcuts() {
    const handleKeyDown = (e) => {
      if (!this.authStore?.isAuthenticated) {
        // List of blocked key combinations
        const blockedKeys = [
          { ctrl: true, shift: true, key: "I" }, // Dev Tools
          { ctrl: true, shift: true, key: "J" }, // Console
          { ctrl: true, shift: true, key: "C" }, // Inspector
          { ctrl: true, key: "u" }, // View Source
          { key: "F12" }, // Dev Tools
          { ctrl: true, key: "s" }, // Save
          { ctrl: true, key: "a" }, // Select All
          { ctrl: true, key: "p" }, // Print
          { ctrl: true, key: "f" }, // Find
          { ctrl: true, key: "h" }, // History
        ];

        const isBlocked = blockedKeys.some((blocked) => {
          return (
            (!blocked.ctrl || e.ctrlKey) &&
            (!blocked.shift || e.shiftKey) &&
            (!blocked.alt || e.altKey) &&
            blocked.key.toLowerCase() === e.key.toLowerCase()
          );
        });

        if (isBlocked) {
          e.preventDefault();
          e.stopPropagation();
          this.handleSecurityBreach(`Blocked shortcut: ${e.key}`);
          return false;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    this.cleanupFunctions = this.cleanupFunctions || [];
    this.cleanupFunctions.push(() =>
      document.removeEventListener("keydown", handleKeyDown, true)
    );
  }

  // Initialize all security measures
  init() {
    if (!SECURITY_ENABLED) {
      console.log("Security Manager is disabled via configuration");
      return;
    }

    if (!this.authStore) {
      this.authStore = useAuthStore();
    }

    if (!this.securityActive) {
      this.securityActive = true;
      this.cleanupFunctions = [];

      this.obfuscateConsole();
      this.startDebuggingDetection();
      this.monitorWindowSize();
      this.disableShortcuts();
    }
  }

  // Cleanup all security measures
  cleanup() {
    if (this.securityActive) {
      this.securityActive = false;

      // Restore console
      this.restoreConsole();

      // Clear intervals
      this.intervals.forEach((interval) => clearInterval(interval));
      this.intervals = [];

      // Remove event listeners
      if (this.cleanupFunctions) {
        this.cleanupFunctions.forEach((cleanup) => cleanup());
        this.cleanupFunctions = [];
      }

      // Remove security overlay
      const overlay = document.getElementById("security-overlay");
      if (overlay) {
        overlay.remove();
      }

      // Restore app element
      const appElement = document.getElementById("app");
      if (appElement) {
        appElement.style.filter = "none";
        appElement.style.pointerEvents = "auto";
      }
    }
  }
}

// Create singleton instance
export const securityManager = new SecurityManager();
